import {
	useState,
	useEffect,
	useMemo,
} from 'react'
import prop from 'ramda/src/prop'
import path from 'ramda/src/path'
import length from 'ramda/src/length'
import pipe from 'ramda/src/pipe'
import propEq from 'ramda/src/propEq'
import find from 'ramda/src/find'
import when from 'ramda/src/when'
import is from 'ramda/src/is'
import equals from 'ramda/src/equals'
import assoc from 'ramda/src/assoc'
import moment from 'moment'
import { ADD_DROP_EVENT_ROUTE_ID } from 'root/src/shared/descriptions/routes/routeIds'
import dropEventConnector from 'root/src/client/logic/dropEvent/connectors/dropEventConnector'
import backArrow from 'root/src/client/public/icons/ic-back-arrow.svg'
import FixedButton from 'root/src/client/web/base/CustomButton/FixedButton'
import FAQSection from 'root/src/client/web/static/FAQ/FAQSection'
import MaxWidthContainer from 'root/src/client/web/base/MaxWidthContainer'
import { discordUrl } from 'root/src/shared/constants/mail'
import { dropEventSubscriptionStatusEnum } from 'root/src/shared/constants/dropEvent'
import isPastToday from 'root/src/shared/util/isPastToday'
import DropWidget from 'root/src/client/web/base/DropWidget'
import { SPONSORSHIP_DETAILS_MODULE_ID } from 'root/src/shared/descriptions/modules/moduleIds'
import apiRequest from 'root/src/client/logic/api/thunks/apiRequest'
import { DROP_GET_EVENT } from 'root/src/shared/descriptions/endpoints/endpointIds'
import dropEndsIn from 'root/src/client/public/dropEndsIn.svg'
import dropComingSoon from 'root/src/client/public/dropComingSoon.svg'
import { faqAccordionIds } from 'root/src/shared/constants/accordionIds'
import { useMaxDaresByPlatform } from 'root/src/client/logic/dropEvent/hooks/useMaxDaresByPlatform'
import { RaceForRewards } from 'root/src/client/v2/drop/features/RaceForRewards'
import LiveCountdown from './LiveCountdown'
import HeroSection from './HeroSection'
import DaresSection from './DaresSection'
import HowToSection from './HowToSection'
import content from './eventStaticContent'
import { DropEventContext } from './contexts/dropEvent.context'

const isDropActive = pipe(
	prop('status'),
	equals(dropEventSubscriptionStatusEnum.ACTIVE),
)

const faqContent = ({
	gameName,
	minTwitchCCVRequired,
	canAccept,
	daresCount,
	maxDares,
}) => ({
	items: [
		{
			id: faqAccordionIds.WHAT_ARE_DROPS,
			title: 'What are drops?',
			content: 'Drops are a series of dares created by video game publishers where creators can win rewards for fans.',
		},
		{
			id: faqAccordionIds.WHAT_ARE_DARES,
			title: 'What are dares?',
			content: 'Dares are challenges for creators to complete that are rewarded with loot. Game keys, DLC, swag, and more with the biggest prizes going to the most difficult dares!',
		},
		{
			id: faqAccordionIds.WHAT_DO_I_NEED_TO_PARTICIPATE,
			title: 'What do I need to participate?',
			content: `The minimum CCV required for this drop is ${minTwitchCCVRequired}!${canAccept ? `Based on your CCV of ${daresCount} you can accept ${maxDares} Dares` : ''}`,
		},
		{
			id: faqAccordionIds.WHERE_CAN_I_GET_GAME,
			title: `Where can I get ${gameName}?`,
			content: 'Once you accept your first dare you’ll receive an email from us with your key if it’s needed to play. You can also access your keys in the <a href="https://daredrop.com/my-keys" target="_blank">My Keys</a> page on Dare Drop.',
		},
		{
			id: faqAccordionIds.HOW_MANY_DARES_CAN_I_DO,
			title: 'How many dares can I do?',
			content: 'Accept any/all dares that you think you can complete and take on the challenge! Some games limit the number of dares you are eligible for based on the size of your channel.',
		},
		{
			id: faqAccordionIds.HOW_DO_I_VIEW_MY_ACCEPTED_COMPLETED_DARES,
			title: 'How do I view my accepted/completed dares?',
			content: 'You can view your current dares and their status by going to “My Profile” in the drop down at the top right corner of the page.',
		},
		{
			id: faqAccordionIds.HOW_DO_I_COMPLETE_A_DARE,
			title: 'How do I complete a dare?',
			content: 'After completing the dare on your channel, you can upload your clip by clicking “Deliver Dare Video” for the given dare in your Profile in the drop down menu on the top right. \n\nOnce uploaded, our team will review your clip, verify it’s completion, and send you your reward!',
		},
		{
			id: faqAccordionIds.WILL_I_GET_PENALIZED_FOR_FAILING_NOT_COMPLETING_A_DARE,
			title: 'Will I get penalized for failing/not completing a dare?',
			content: 'No. You’re only obligated to stream or post content for the game when you claim a key.',
		},
		{
			id: faqAccordionIds.HOW_DO_I_ACCESS_MY_REWARDS,
			title: 'How do I access my rewards?',
			content: 'Once a completed dare is verified you will receive an email from Dare Drop with your rewards. You can also visit the <a href="https://daredrop.com/my-keys" target="_blank">My Keys</a> page on Dare Drop to view both Initial Keys and your Key Rewards.',
		},
		{
			id: faqAccordionIds.IS_THIS_OFFER_AVAILABLE_IN_ALL_REGIONS,
			title: 'Is this offer available in all regions?',
			content: 'If there are keys listed for a specific region, then those drops are only for that region. If no specific region is listed, it’s available for everyone!',
		},
		{
			id: faqAccordionIds.WHAT_IF_I_HAVE_MORE_QUESTIONS_OR_NEED_HELP,
			title: 'What if I have more questions or need help?',
			content: `Join our discord at <a href="https://${discordUrl}" target="_blank">${discordUrl}</a> or shoot us an email at <a href = "mailto: support@daredrop.com">support@daredrop.com</a>`,
		},
	],
})

const styles = {
	root: {
		display: 'block',
	},
	fullSize: {
		width: '100%',
		height: '100%',
	},
	flex: {
		display: 'flex',
	},
	flexCenter: {
		justifyContent: 'center',
		alignItems: 'center',
	},
}

const DropEvent = ({
	classes, preview, isAdmin, dropEvent, platforms, isAuthenticated,
	isBrand, userId, ccv, setInput, redirectAndPrePopulate, regions, setEmailSubscriptionDrop,
	displayModal, userCampaignData, canEarnPoints, dispatch,
	currentRouteParams, tokens, isCreator, displayHowToEarnPointsModal,
	followerCountByPlatforms, parentUserId,
}) => {
	const {
		dares: dropDares, followersBreakpoints, ccvBreakpoints, totalPrizes,
		id: campaignId, assets, gameDescriptionBody, liveDate, isEmailSubscribed,
		initialProduct, isGameFree, mnemonicId, expirationDate, dropTitle, estimatedPointsToEarn,
		canUserReclaimReservation, pointsLimit, pointsAwarded, pointsReserved, isAbleToParticipate, isCommunityDrop,
	} = dropEvent || {}

	const maxDares = useMaxDaresByPlatform({ dropEvent })

	const canAccept = !isAdmin && !isBrand && maxDares > 0

	const refetchDropEvent = async () => {
		const routeParamsMnemonicId = currentRouteParams?.recordId
		return dispatch(
			apiRequest(DROP_GET_EVENT, { recordId: routeParamsMnemonicId }),
		)
	}

	const [submitting, setSubmitting] = useState(false)
	const [subscribed, setSubscribed] = useState(isEmailSubscribed)
	const [comingSoon, setComingSoon] = useState(!isPastToday(liveDate))
	const [customDate, setCustomDate] = useState()

	const showTermsModal = !userCampaignData?.termsAccepted
		&& canEarnPoints
		&& !isAdmin
		&& !isBrand
		&& pointsLimit
		&& pointsLimit - pointsAwarded > 0
	const dares = useMemo(() => dropDares?.map((dare, idx) => assoc('id', idx, dare)) || [], [dropDares])

	useEffect(() => {
		if (showTermsModal) {
			displayHowToEarnPointsModal()
		}
	}, [showTermsModal])

	const toggleEmailSubscription = async (setError) => {
		try {
			await setEmailSubscriptionDrop(campaignId, !subscribed, dropEvent)
			setSubscribed(prev => !prev)
		} catch (err) {
			setError(err.error)
		}
	}
	const gameName = path(['gameObj', 'label'], dropEvent) || path(['campaignProduct'], dropEvent)

	const isDropOwner = [userId, parentUserId].includes(dropEvent.brand.id)

	const twitchFollowersRequired = pipe(
		prop('followersBreakpoints'),
		when(
			is(Array),
			pipe(
				find(propEq('maxDares', 0)),
				prop('followerCount'),
			),
		),
	)(dropEvent)

	const minTwitchCCVRequired = useMemo(() => {
		if (ccvBreakpoints.length) {
			const min = ccvBreakpoints.reduce((prev, curr) => (prev.ccv < curr.ccv ? prev : curr))
			return min.ccv + 1
		}
		return 0
	}, [ccvBreakpoints])

	const toggleComingSoon = () => {
		setComingSoon(prevState => !prevState)
		if (isAdmin && isPastToday(liveDate)) setCustomDate(moment().add(20000, 'minutes'))
	}

	const pointsToEarn = comingSoon ? undefined : estimatedPointsToEarn
	const isExpired = isPastToday(expirationDate)
	const isCreatorAcceptedDrop = !!userCampaignData?.creatorAcceptedDares && userCampaignData.creatorAcceptedDares > 0

	const displaySponsorshipDetailsModal = () => {
		displayModal(SPONSORSHIP_DETAILS_MODULE_ID, {
			maxDares,
			canEarnPoints,
			isInitialProduct: !!initialProduct && initialProduct.length > 0,
			acceptedDares: userCampaignData?.creatorAcceptedDares,
			pointsReservationDueDate: userCampaignData?.termsAccepted?.pointsReservationDueDate,
			reservedPointsTotal: userCampaignData?.termsAccepted?.reservedPointsTotal,
			termsAcceptedDate: userCampaignData?.termsAccepted?.created,
			pointsEarned: userCampaignData?.pointsEarned,
			isCreatorAcceptedDrop,
			canUserReclaimReservation,
		})
	}

	const dropEventContextValue = useMemo(() => ({
		subscribed,
		toggleEmailSubscription,
		dropEvent,
	}), [subscribed, toggleEmailSubscription, dropEvent])

	return (
		<DropEventContext.Provider value={dropEventContextValue}>
			{comingSoon && (
				<LiveCountdown
					date={customDate || liveDate}
					onFinish={async () => {
						await refetchDropEvent()
						setComingSoon(false)
					}}
					headerText="Drop is coming soon"
					bannerImage={dropComingSoon.src}
				/>
			)}
			{!comingSoon && !isExpired && (
				<LiveCountdown
					date={expirationDate}
					onFinish={async () => {
						await refetchDropEvent()
					}}
					headerText="Drop ends in"
					bannerImage={dropEndsIn.src}
				/>
			)}
			<DropWidget {...{ mnemonicId, isAdmin, comingSoon, toggleComingSoon }} />
			<MaxWidthContainer className={classes.root}>
				{preview && (
					<FixedButton
						label="Back"
						image={backArrow.src}
						onClick={() => redirectAndPrePopulate({
							prePopulateData: dropEvent,
							redirectRouteId: ADD_DROP_EVENT_ROUTE_ID,
							setInput,
						})}
					/>
				)}
				<HeroSection
					assets={assets}
					gameName={dropTitle || gameName}
					brandName={path(['brand', 'brandName'], dropEvent)}
					gameDescriptionBody={gameDescriptionBody}
					regions={regions}
					platforms={platforms}
					dares={dares}
					campaignId={campaignId}
					submitting={submitting}
					setSubmitting={setSubmitting}
					availablePrizes={{
						lootRewards: prop('availablePrizes', dropEvent),
						initialProduct: prop('initialProduct', dropEvent),
					}}
					isDropOver={!isDropActive(dropEvent) && !preview && !isAdmin && !isDropOwner}
					isBrand={isBrand}
					isCreator={isCreator}
					status={prop('status', dropEvent)}
					redirectAndPrePopulate={redirectAndPrePopulate}
					liveDate={liveDate}
					displayModal={displayModal}
					comingSoon={comingSoon}
					isAuthenticated={isAuthenticated}
					emailSubscription={subscribed}
					toggleEmailSubscription={toggleEmailSubscription}
					totalPrizes={totalPrizes}
					initialProduct={initialProduct}
					pointsToEarn={pointsToEarn}
					isGameFree={isGameFree}
					canEarnPoints={canEarnPoints}
					pointsLimit={pointsLimit}
					pointsAwarded={pointsAwarded}
					isAdmin={isAdmin}
					displaySponsorshipDetailsModal={displaySponsorshipDetailsModal}
					isCreatorAcceptedTerms={userCampaignData?.termsAccepted}
					isCreatorAcceptedDrop={isCreatorAcceptedDrop}
					pointsReserved={pointsReserved}
					showTermsModal={showTermsModal}
					canUserReclaimReservation={canUserReclaimReservation}
					displayHowToEarnPointsModal={displayHowToEarnPointsModal}
					isAbleToParticipate={isAbleToParticipate}
					isCommunityDrop={isCommunityDrop}
				/>
				<RaceForRewards />
				{
					!isAuthenticated && (
						<HowToSection
							pageContent={prop('howToSection', content)}
							displayModal={displayModal}
						/>
					)
				}
				{dares && (
					<DaresSection
						{...{
							dares,
							gameName,
							campaignId,
							submitting,
							setSubmitting,
							content: prop('daresSection', content),
							comingSoon,
							emailSubscription: subscribed,
							toggleEmailSubscription,
							showTermsModal,
							pointsToEarn,
							canEarnPoints,
							tokens,
							dropEvent,
							followerCountByPlatforms,
							averageCcv: ccv,
							followersBreakpoints,
							ccvBreakpoints,
							isAuthenticated,
							isBrand,
							creatorAcceptedDares: userCampaignData?.creatorAcceptedDares,
							isAbleToParticipate,
							canAccept,
						}}
					/>
				)}
				<FAQSection
					pageContent={faqContent({
						gameName,
						twitchFollowersRequired,
						daresAmount: length(dares),
						initialProduct: prop('initialProduct', dropEvent),
						minTwitchCCVRequired,
						canAccept,
						daresCount: dares.length,
						maxDares: maxDares > dares.length ? dares.length : maxDares,
					})}
				/>
			</MaxWidthContainer>
		</DropEventContext.Provider>
	)
}

export default dropEventConnector(DropEvent, styles, 'MuiDareEvent')
